import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, ShieldAlert, KeyRound } from 'lucide-react';
import { ForcePasswordChange } from './ForcePasswordChange';
import { ROUTE_MODULES, ModuleId } from '@/lib/rolePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, initialized } = useAuthContext();
  const { canAccess, loading: roleLoading, hasAnyRole } = useUserRole();
  const location = useLocation();

  // Use react-query for profile check - cached, no waterfall
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['profile-password-check', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('must_change_password')
        .eq('id', user!.id)
        .maybeSingle();
      return { mustChangePassword: error ? false : data?.must_change_password ?? false };
    },
    enabled: !!user && initialized,
    staleTime: 1000 * 60 * 10, // Cache for 10 min
  });

  // Defer 2FA check - load lazily after initial render
  const { data: twoFactorData } = useQuery({
    queryKey: ['2fa-check', user?.id],
    queryFn: async () => {
      const [settingsResult, mfaResult, profileResult] = await Promise.all([
        supabase.from('system_settings').select('setting_value').eq('setting_key', 'require_2fa').is('project_id', null).maybeSingle(),
        supabase.auth.mfa.listFactors(),
        supabase.from('profiles').select('first_login_at').eq('id', user!.id).single(),
      ]);

      const require2fa = (settingsResult.data?.setting_value as { enabled: boolean })?.enabled ?? false;
      const isEnrolled = (mfaResult.data?.totp?.filter(f => f.status === 'verified') ?? []).length > 0;
      const firstLoginAt = (profileResult.data as any)?.first_login_at as string | null;

      let isGracePeriodExpired = false;
      if (require2fa && !isEnrolled && firstLoginAt) {
        const deadline = new Date(new Date(firstLoginAt).getTime() + 14 * 24 * 60 * 60 * 1000);
        isGracePeriodExpired = new Date() > deadline;
      }

      return { require2fa, isEnrolled, isGracePeriodExpired };
    },
    enabled: !!user && initialized,
    staleTime: 1000 * 60 * 5,
  });

  const mustChangePassword = profileData?.mustChangePassword ?? false;
  const require2fa = twoFactorData?.require2fa ?? false;
  const isEnrolled = twoFactorData?.isEnrolled ?? false;
  const isGracePeriodExpired = twoFactorData?.isGracePeriodExpired ?? false;

  // Only block on critical checks: auth + role + profile password
  if (loading || !initialized || profileLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (mustChangePassword) {
    return <ForcePasswordChange onSuccess={() => {}} />;
  }

  // 2FA check - non-blocking initially, only blocks when data is loaded
  if (require2fa && !isEnrolled && isGracePeriodExpired && !location.pathname.includes('/einstellungen')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-destructive/10">
              <KeyRound className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle>Zwei-Faktor-Authentifizierung erforderlich</CardTitle>
            <CardDescription>
              Ihr Unternehmen verlangt die Einrichtung einer Zwei-Faktor-Authentifizierung. Die 14-tägige Frist ist abgelaufen. Bitte richten Sie 2FA in Ihren Einstellungen ein, um fortzufahren.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button asChild variant="brand">
              <Link to="/dashboard/einstellungen">Jetzt einrichten</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!hasAnyRole()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-orange-500/10">
              <ShieldAlert className="h-8 w-8 text-orange-500" />
            </div>
            <CardTitle>Keine Rolle zugewiesen</CardTitle>
            <CardDescription>
              Ihrem Account wurde noch keine Rolle zugewiesen. Bitte kontaktieren Sie einen Administrator.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button asChild variant="outline">
              <Link to="/">Zur Startseite</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Module access check
  const currentPath = location.pathname;
  let moduleId: ModuleId | null = null;

  if (ROUTE_MODULES[currentPath]) {
    moduleId = ROUTE_MODULES[currentPath];
  } else {
    for (const [path, module] of Object.entries(ROUTE_MODULES)) {
      if (currentPath.startsWith(path) && path !== '/dashboard') {
        moduleId = module;
        break;
      }
    }
  }

  if (moduleId && !canAccess(moduleId)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-destructive/10">
              <ShieldAlert className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle>Kein Zugriff</CardTitle>
            <CardDescription>
              Sie haben keine Berechtigung, diesen Bereich zu öffnen. Bitte kontaktieren Sie einen Administrator, falls Sie Zugriff benötigen.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button asChild>
              <Link to="/dashboard">Zur Übersicht</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
